// Shared period-selection logic for the Driving and Drivers tabs.
// Pure helpers (no React) so both tabs compute identical windows.

export function todayStr() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

export function dayBounds(startStr, endStr) {
  // Native date inputs can be temporarily empty while editing. Keep a valid
  // window and normalize reversed dates before advancing the inclusive end day.
  const validDay = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
    const parsed = new Date(`${value}T00:00:00`);
    const [year, month, day] = value.split("-").map(Number);
    return parsed.getFullYear() === year && parsed.getMonth() + 1 === month && parsed.getDate() === day ? value : null;
  };
  const first = validDay(startStr) || validDay(endStr) || todayStr();
  const last = validDay(endStr) || first;
  const s = new Date(`${first < last ? first : last}T00:00:00`);
  const e = new Date(`${first < last ? last : first}T00:00:00`);
  e.setDate(e.getDate() + 1);
  return { start: s.toISOString(), end: e.toISOString() };
}

// Preset windows, relative to now (except calendar month).
export const PRESETS = [
  { key: "1h", label: "1h", hours: 1 },
  { key: "4h", label: "4h", hours: 4 },
  { key: "8h", label: "8h", hours: 8 },
  { key: "12h", label: "12h", hours: 12 },
  { key: "1d", label: "1 day", days: 1 },
  { key: "3d", label: "3 days", days: 3 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "2w", label: "2 weeks", days: 14 },
  { key: "month", label: "Month", month: true },
  { key: "30d", label: "1 month", days: 30 },
  { key: "1y", label: "1 year", days: 365 },
];

// The compact, calendar-based set used by the Driving + Charging tabs. (Custom is
// rendered separately by PeriodSelector.) "This Week" honors the user's week-start
// preference (Sunday or Monday).
export const SIMPLE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

// Week-start preference (client-side, like the US/EU unit toggle). "sun" | "mon".
const WEEK_START_KEY = "tm3_week_start";
// Seed the local cache from the account value so the choice survives storage
// eviction (iOS ITP) and syncs across devices / the installed PWA.
export function hydrateWeekStart(v) {
  if (v !== "sun" && v !== "mon") return;
  try { localStorage.setItem(WEEK_START_KEY, v); } catch (e) { /* ignore */ }
}
export function getWeekStart() {
  try {
    return localStorage.getItem(WEEK_START_KEY) === "mon" ? "mon" : "sun";
  } catch (e) {
    return "sun";
  }
}
export function setWeekStartPref(v) {
  try {
    localStorage.setItem(WEEK_START_KEY, v === "mon" ? "mon" : "sun");
  } catch (e) {
    /* ignore */
  }
}

export function windowFor(key, customStart, customEnd, now = new Date()) {
  const end = new Date(now);
  if (key === "custom") return dayBounds(customStart, customEnd);
  if (key === "today") {
    const s = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
    return { start: s.toISOString(), end: end.toISOString() };
  }
  if (key === "week") {
    const s = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
    const startDow = getWeekStart() === "mon" ? 1 : 0; // 0=Sun, 1=Mon
    let back = s.getDay() - startDow;
    if (back < 0) back += 7;
    s.setDate(s.getDate() - back); // back to the start of the current week
    return { start: s.toISOString(), end: end.toISOString() };
  }
  if (key === "month") {
    const s = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
    return { start: s.toISOString(), end: end.toISOString() };
  }
  const p = PRESETS.find((x) => x.key === key) || PRESETS[8];
  const ms = p.hours ? p.hours * 3600e3 : (p.days || 1) * 86400e3;
  return { start: new Date(end.getTime() - ms).toISOString(), end: end.toISOString() };
}

export function spanDaysOf(startISO, endISO) {
  return (new Date(endISO).getTime() - new Date(startISO).getTime()) / 86400e3;
}

export function fmtRangeCaption(startISO, endISO) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "";
  const opts = { month: "short", day: "numeric" };
  const sameDay = s.toDateString() === e.toDateString();
  return sameDay
    ? s.toLocaleDateString([], opts)
    : `${s.toLocaleDateString([], opts)} – ${e.toLocaleDateString([], opts)}`;
}
